/**
 * Splash-screen artwork manifest + per-test resolution.
 *
 * The cinematic splash (SplashScreen.tsx) shows a circular badge that zooms
 * into the page. Since the ALIS rename (Oct 2026) every splash — landing page
 * and every test page — shows the same ALIS emblem; the per-test MoftakiLab
 * medallions are archived in frontend/branding-backup/moftaki/tests/.
 * The test code is still resolved below because it keys each test's own
 * splash cooldown (when the splash plays), not the artwork.
 *
 * Keyed on the STABLE Test.TestCode (BUG-2026-0197), not the renameable
 * TestName — so renaming a test in the admin Test Catalogue never changes its
 * medallion. Because the splash fires on full page load — before any API data
 * exists — the test CODE for a workOrderDetailKey is remembered in localStorage
 * the first time the test page loads (see cacheTestCodeForWod, called from
 * TestWorkflowPage). The very first cold visit shows the generic badge; every
 * refresh after that shows the test's own medallion.
 */

import { TEST_CODES } from './testCodes';

export const SPLASH_BG_SRC = '/moftaki-splash-screen-full.webp';
export const SPLASH_DEFAULT_CIRCLE_SRC = '/splash/circle-default.webp';

/** Stable Test.TestCode → slug naming that test's splash cooldown stamp. */
const TEST_SPLASH_SLUGS: Record<string, string> = {
  [TEST_CODES.directShear]: 'direct-shear',
  [TEST_CODES.atterberg]: 'atterberg',
  [TEST_CODES.psd]: 'psd',
  [TEST_CODES.triaxialCu]: 'triaxial-cu',
  [TEST_CODES.triaxialCd]: 'triaxial-cd',
  [TEST_CODES.rockTcs]: 'rock-tcs',
  [TEST_CODES.rockBts]: 'rock-bts',
  [TEST_CODES.rockUcsNs]: 'rock-ucs',
  [TEST_CODES.rockUcs]: 'rock-ucsm',
  [TEST_CODES.pointLoad]: 'point-load',
  [TEST_CODES.cerchar]: 'cerchar',
};

/** Fallback slug for tests without their own entry. */
const DEFAULT_TEST_SLUG = 'default-test';

/** Badge slug for a test code (default slug when no dedicated art exists). */
export const testSplashSlug = (testCode: string): string =>
  TEST_SPLASH_SLUGS[testCode] ?? DEFAULT_TEST_SLUG;

/** Every test shows the ALIS emblem (same art as the landing splash). */
export const testSplashCircleSrc = (_testCode: string): string => SPLASH_DEFAULT_CIRCLE_SRC;

// ── workOrderDetailKey → testCode memory (so the splash knows the test
//    synchronously on the NEXT full page load) ─────────────────────────────

const WOD_TEST_CODE_CACHE_KEY = 'belab.splash.wodTestCodes';
const WOD_TEST_CODE_CACHE_MAX = 80;

/**
 * Fired by cacheTestCodeForWod the moment a test page learns its test code.
 * SplashScreen listens for it so the FIRST client-side visit to a test can
 * still play that test's medallion (no cache to read yet at nav time).
 */
export const TEST_CODE_CACHED_EVENT = 'belab:testCodeCached';

export interface TestCodeCachedDetail {
  workOrderDetailKey: string;
  testCode: string;
}

type WodTestCodeCache = Record<string, string>;

const readWodCache = (): WodTestCodeCache => {
  try {
    return JSON.parse(localStorage.getItem(WOD_TEST_CODE_CACHE_KEY) || '{}') as WodTestCodeCache;
  } catch {
    return {};
  }
};

/** Remember which test-code a workOrderDetailKey belongs to (bounded LRU-ish). */
export const cacheTestCodeForWod = (workOrderDetailKey: string, testCode: string): void => {
  if (!workOrderDetailKey || !testCode) return;
  window.dispatchEvent(
    new CustomEvent<TestCodeCachedDetail>(TEST_CODE_CACHED_EVENT, {
      detail: { workOrderDetailKey, testCode },
    }),
  );
  try {
    const cache = readWodCache();
    if (cache[workOrderDetailKey] === testCode) return;
    // Re-insert to move to the end; drop oldest entries beyond the cap.
    delete cache[workOrderDetailKey];
    cache[workOrderDetailKey] = testCode;
    const keys = Object.keys(cache);
    for (let i = 0; i < keys.length - WOD_TEST_CODE_CACHE_MAX; i++) delete cache[keys[i]];
    localStorage.setItem(WOD_TEST_CODE_CACHE_KEY, JSON.stringify(cache));
  } catch {
    /* private mode — splash just falls back to the generic badge */
  }
};

export const getCachedTestCodeForWod = (workOrderDetailKey: string): string | null =>
  readWodCache()[workOrderDetailKey] ?? null;
